/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { State } from "store/reducers";
import ls from "Localization";
import _ from "lodash";
import { Helmet } from "react-helmet-async";

import actions from "store/actions";

import dayjs from "dayjs";
import Report from "Components/Report";
import { dateSort } from "Components/Report/helpers";
import { toStringCurrency } from "helpers/string";

import ReactTable from "react-table";
import { Checkbox, Fab, Tooltip } from "@mui/material";
import RedoOutlinedIcon from "@mui/icons-material/RedoOutlined";
import DateSelectFilter from "Components/Report/DateSelectorFilter";
import { DasnState } from "store/reducers/dasn";
import IDasn, { DasnStatus } from "Types/Dasn";
import { FaRegFilePdf } from "react-icons/fa";

import ImportModal from "./Import";
import SendModal from "./Send";

const ls2: any = ls;

let getDasesDebounced: (() => any) & _.Cancelable = _.debounce(() => {}, 250);

function Dasn() {
	const [checked, setChecked] = useState<string[]>([]);
	const [messageModal, setMessageModal] = useState<React.ReactNode | null>(
		null
	);

	const dispatch = useDispatch<any>();
	const table = useRef<ReactTable<any>>(null);

	const state = useSelector<State, DasnState>(s => s.dasn);

	const handleFetchData = (tableState: any) => {
		if (!tableState) return;
		const { page, pageSize, toExport, callback } = tableState;
		let { sorted, filtered } = tableState;

		if (getDasesDebounced) {
			getDasesDebounced.cancel();
		}

		if (!sorted) sorted = [];

		const creationDate = filtered.find((c: any) => c.id === "creationDate");

		if (creationDate?.value) {
			filtered = filtered.filter((c: any) => c.id !== "creationDate");

			if (creationDate?.value.startDate) {
				filtered.push({
					id: "startDate",
					value: creationDate.value.startDate
				});
			}

			if (creationDate?.value.endDate)
				filtered.push({
					id: "endDate",
					value: creationDate.value.endDate
				});
		}

		getDasesDebounced = _.debounce(
			() =>
				dispatch(
					actions.dasn.getDasn(
						page * pageSize,
						pageSize,
						filtered.reduce(
							(p: any, c: any) => p + `&filters[${c.id}]=${c.value}`,
							""
						),
						sorted[0]?.id,
						sorted[0]?.desc,
						toExport,
						callback
					)
				),
			500
		);

		getDasesDebounced();
	};

	const load = () => handleFetchData(table.current?.state);

	const columns = [
		{
			Header: ls.client,
			id: "mei",
			show: true,
			accessor: (c: any) => {
				if (c.mei) {
					return c.mei.businessName;
				}
				return "";
			}
		},
		{
			Header: "CNPJ",
			id: "cnpj",
			show: true,
			accessor: (c: any) => c.mei?.cnpj,
			width: 150
		},
		{
			Header: ls.serviceValue,
			id: "serviceValue",
			accessor: (c: IDasn) => toStringCurrency((c.serviceValue || 0) / 100.0),
			show: true,
			width: 150
		},
		{
			Header: ls.productValue,
			id: "productValue",
			accessor: (c: IDasn) => toStringCurrency(c.productValue || 0 / 100.0),
			show: true,
			width: 150
		},
		{
			Header: ls.year,
			id: "year",
			accessor: (c: IDasn) => c.year || "",
			show: true,
			width: 150
		},
		{
			Header: ls.dueDate,
			id: "dueDate",
			accessor: (c: IDasn) =>
				c.year
					? dayjs(new Date(c.year, 4, 31))
							.add(1, "year")
							.format(ls.dateFormatShort)
					: "",
			show: true,
			sortMethod: dateSort,
			width: 150,
			Filter: p => <DateSelectFilter {...p} />
		},

		{
			Header: ls.receipt,
			id: "receipt",
			show: false,
			accessor: (c: any) => c.receipt,
			width: 150
		},
		{
			Header: ls.status,
			id: "status",
			show: true,
			accessor: (c: IDasn) => {
				if (c.status !== undefined) {
					if (c.status === DasnStatus.due) {
						return (
							<Tooltip title={c.status}>{ls2[DasnStatus[c.status]]}</Tooltip>
						);
					}
					return ls2[DasnStatus[c.status]];
				}
			},
			width: 140,
			Filter: ({ filter, onChange }: any) => {
				return (
					<select
						onChange={event => onChange(event.target.value)}
						style={{ width: "100%" }}
						value={filter ? filter.value : "all"}
					>
						<option value="">Todos</option>
						{Object.keys(DasnStatus)
							.filter(c => !isNaN(Number(c)))
							.map((c: any) => (
								<option key={c} value={c}>
									{ls2[DasnStatus[c]]}
								</option>
							))}
					</select>
				);
			}
		},
		{
			Header: ls.actions,
			id: "actions",
			accessor: (c: any) => (
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flex: 1
					}}
				>
					{c.receipt && (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								flex: 1
							}}
						>
							<a href={c.receipt} target="_blank">
								<FaRegFilePdf />
							</a>
						</div>
					)}
					{/* <a
						href="!#"
					>
						<Tooltip title="Enviar:">
							<RedoOutlinedIcon />
						</Tooltip>
					</a> */}
				</div>
			),
			show: true,
			filterable: false,
			width: 80
		}
	];

	return (
		<div>
			<Helmet>
				<title>Declaração Anual do Simples Nacional | {ls.appName}</title>
			</Helmet>
			<Report
				manual
				tableRef={table}
				headerRightComponent={
					<>
						<SendModal load={load} />
						<ImportModal load={load} />
					</>
				}
				titleText="Declaração Anual do Simples Nacional"
				title="Declaração Anual do Simples Nacional"
				data={state.items}
				pages={state.pages}
				loading={state.loading.getAll}
				columns={columns}
				onFetchData={handleFetchData}
				filterable
				visibleColumns={columns
					.filter(c => c.show)
					.map(c => c.Header.toString())}
				defaultSorted={[
					{
						id: "createdAt",
						desc: true
					}
				]}
				getTdProps={() => {
					return {
						style: {
							display: "flex",
							alignItems: "center"
						}
					};
				}}
			/>

			{messageModal}
			{checked.length >= 1 && (
				<Tooltip title="Enviar para:">
					<Fab
						color="primary"
						aria-label="Send"
						style={{
							position: "fixed",
							bottom: "16px",
							right: "16px",
							zIndex: 1000
						}}
					>
						<RedoOutlinedIcon />
					</Fab>
				</Tooltip>
			)}
		</div>
	);
}

export default Dasn;
